import { HostAccountType } from './IHostProfile';

export type ServiceProviderIdVerificationStatus = 'NotStarted' | 'Pending' | 'Verified' | 'Rejected';

/** Owner-only shape (GET/POST .../service-provider-profile/me) -- carries the full legal block. NEVER used for a future public-facing endpoint. */
export interface IServiceProviderProfile {
  id: string;
  userId: string;

  /** Set only for an employed provider -- always null here, since ServiceProviderProfileController only builds the independent, self-registration modality (a host adding one of their own is a separate, not-yet-built flow). */
  hostUserId?: string;
  fullName: string;
  email: string;
  /** Optional day-to-day contact number for this profile -- distinct from contactPhone below, which is this person's fiscal-identity phone on UserBillingProfile (see that entity's own doc comment). */
  phone?: string;
  /** Individual | Business -- see UserBillingProfile.AccountType's own doc comment. Resolved from UserBillingProfile, not a column of this profile itself. */
  accountType: HostAccountType;
  legalName: string;
  taxId?: string;
  billingAddress: string;
  contactPhone: string;
  /** true (default): booking any one of this provider's Services blocks the same slot across all of them. */
  sharedAvailability: boolean;
  /**
   * True when this professional charges for going to look at the job, whether
   * or not the work is then contracted -- see ServiceProviderProfile.
   * ChargesVisitFee on the backend. Off for everybody who has not deliberately
   * turned it on.
   */
  chargesVisitFee: boolean;
  /** What the visit costs, in visitFeeCurrencyCode below. Meaningless while chargesVisitFee is false, and zeroed by the server when it is. */
  visitFeeAmount: number;
  /** ISO 4217 the figure above is in -- derived from this provider's own listings on the backend, not a column of the profile. */
  visitFeeCurrencyCode: string;
  stripeConnectedAccountId?: string;
  /**
   * Set only while the platform has refused to carry this provider. Nobody
   * verifies a provider here -- the platform is no authority on who really
   * is a gardener or an electrician (see ServiceProviderProfile.SuspendedAt
   * on the backend).
   */
  suspendedAt?: string;
  suspensionReason?: string;
  /** Stripe Connect onboarding state -- independent of the suspension above. */
  idVerificationStatus: ServiceProviderIdVerificationStatus;

  displayName: string;
  profilePhotoUrl?: string;
  bio?: string;
  verifiedBadge: boolean;

  createdAt: string;
  updatedAt: string;
}

/**
 * Self-registration, independent modality only -- see
 * ServiceProviderProfileController's own doc comment. No hostUserId here on
 * purpose. accountType/legalName/taxId/contactPhone/billingAddress are only
 * actually required by the backend when this person has no UserBillingProfile
 * yet (reused as-is from their other role otherwise, see IdentityLegalStep's
 * own doc comment on existingBilling) -- kept non-optional here to match
 * IHostProfile's own ICreateHostProfileRequest shape, same "harmless to send
 * undefined when reusing" convention that request already relies on.
 */
export interface ICreateServiceProviderProfileRequest {
  fullName: string;
  phone?: string;
  accountType: HostAccountType;
  legalName: string;
  taxId?: string;
  contactPhone: string;
  billingAddress: string;
  displayName: string;
  profilePhotoUrl?: string;
  bio?: string;
  /** Omit to default to true (one shared calendar across every Service this provider offers). */
  sharedAvailability?: boolean;
  /**
   * Whether they charge for going to look at a job, and how much -- asked at
   * sign-up because it is a decision about what they are selling rather than a
   * setting to stumble on later. Omit to leave it off, which is the default.
   */
  chargesVisitFee?: boolean;
  /** Required by the backend (and greater than zero) only when chargesVisitFee is true. */
  visitFeeAmount?: number;
}

/** POST .../service-provider-profile/verification/start's response -- same shape as HostProfile's own IStripeOnboardingResponse, just carrying a ServiceProviderProfile instead. */
export interface IServiceProviderStripeOnboardingResponse {
  accountSessionClientSecret?: string;
  profile: IServiceProviderProfile;
}

/**
 * PUT .../service-provider-profile/visit-fee, both halves at once -- "I charge"
 * with no figure is not a state worth saving, so the endpoint takes and returns
 * the whole setting rather than patching one field.
 */
export interface IVisitFee {
  chargesVisitFee: boolean;
  visitFeeAmount: number;
  /** ISO 4217 the figure is in, so the screen never shows a bare number. */
  currencyCode: string;
}

/**
 * The same typo bound the backend enforces (ServiceProviderProfileController.
 * MaxVisitFeeAmount). Checked here so 4000 instead of 40 is caught in front of
 * them rather than by a round trip -- it is a slipped decimal point guard, not
 * a rule about what a visit is worth.
 */
export const MAX_VISIT_FEE_AMOUNT = 1000;
